/**
 * Phase 4 Slice 2 — horn inbox + seasonal delay helpers.
 * Pure log of who sounded / answered / delayed / arrived.
 * Quest-shaped liege objectives land in Slice 3; AI need-horns in Slice 5.
 */
import type { Season } from "./clock.ts";
import type { CallOutcome, DistanceBand } from "./call.ts";
import type { TroopStacks } from "./troops.ts";

export type HornKind =
  | "liege-call"
  | "liege-answer"
  | "banner-call"
  | "banner-reply"
  | "banner-delay"
  | "arrival"
  | "system";

export interface HornEntry {
  id: string;
  /** Game turn when the entry was posted (or last updated). */
  turn: number;
  season?: Season;
  kind: HornKind;
  title: string;
  body: string;
  actorKey?: string;
  actorName?: string;
  outcome?: CallOutcome;
  /** Unread until the player opens the inbox / end-turn digest. */
  read: boolean;
  /** Still waiting on a delayed banner reply. */
  pending?: boolean;
  /** Turns remaining when this is an open delay card. */
  turnsLeft?: number;
}

export interface HornInbox {
  entries: HornEntry[];
  /** Monotonic id counter. */
  nextId: number;
}

/** Bannerman promised a late answer — resolves over End turns. */
export interface PendingBannerReply {
  key: string;
  name: string;
  turnsLeft: number;
  /** Spears reserved from their garrison when they delayed (headcount mirror). */
  committed: number;
  /**
   * Typed composition reserved for the delayed column.
   * When absent (legacy saves), arrival treats `committed` as all-MAA.
   */
  stacksCommitted?: TroopStacks;
  reason: string;
  seasonAtCall: Season;
  distanceBand: DistanceBand;
  /** Turn the delay was posted. */
  postedTurn: number;
  /** Matching horn entry id (delay card). */
  hornId?: string;
}

export const HORN_INBOX_CAP = 48;

export function createHornInbox(): HornInbox {
  return { entries: [], nextId: 1 };
}

/**
 * How many End turns a "delay" banner reply waits before the levy rides.
 * Winter/Autumn slower; far holds add a beat; near shaves one (min 1).
 */
export function bannerDelayTurns(
  season: Season,
  band: DistanceBand = "mid",
): number {
  let base =
    season === "Winter" ? 3 : season === "Autumn" ? 2 : season === "Spring" ? 2 : 1;
  if (band === "far") base += 1;
  if (band === "near") base = Math.max(1, base - 1);
  return Math.max(1, base);
}

export function postHorn(
  inbox: HornInbox,
  partial: Omit<HornEntry, "id" | "read"> & { read?: boolean },
): { inbox: HornInbox; entry: HornEntry } {
  const entry: HornEntry = {
    ...partial,
    id: `horn-${inbox.nextId}`,
    read: partial.read ?? false,
  };
  const entries = [entry, ...inbox.entries].slice(0, HORN_INBOX_CAP);
  return {
    inbox: { entries, nextId: inbox.nextId + 1 },
    entry,
  };
}

export function markHornRead(inbox: HornInbox, id: string): HornInbox {
  return {
    ...inbox,
    entries: inbox.entries.map((e) =>
      e.id === id ? { ...e, read: true } : e,
    ),
  };
}

export function markAllHornsRead(inbox: HornInbox): HornInbox {
  return {
    ...inbox,
    entries: inbox.entries.map((e) => (e.read ? e : { ...e, read: true })),
  };
}

/** Patch a live delay card when turns tick. */
export function updateHornEntry(
  inbox: HornInbox,
  id: string,
  patch: Partial<HornEntry>,
): HornInbox {
  return {
    ...inbox,
    entries: inbox.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)),
  };
}

export function unreadHornCount(inbox: HornInbox): number {
  return inbox.entries.reduce((n, e) => n + (e.read ? 0 : 1), 0);
}

export function pendingHornCount(inbox: HornInbox): number {
  return inbox.entries.reduce((n, e) => n + (e.pending ? 1 : 0), 0);
}

/** Newest-first digest lines for chrome / tests. */
export function hornDigest(inbox: HornInbox, limit = 8): string[] {
  return inbox.entries.slice(0, limit).map((e) => {
    const flag = e.pending
      ? `[delay ${e.turnsLeft ?? "?"}t] `
      : e.read
        ? ""
        : "• ";
    return `${flag}${e.title}: ${e.body}`;
  });
}
